// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE LOAN OFFICER'S GUIDED SUBMISSION (LO plan v7, Jesse 2026-10-28), inside DoorlyPro, sidebar kept.
//
// About your borrower → The home and the loan → Co-borrower (only when there is one) → Employment and income (which
// sources; one screen per rental) → Documents → Calculated income & assets → Review and submit. The borrower's guided
// pattern: "N of M" and the step's name, the progress segments, one card per screen, Back and Continue, saved on the
// server on every Continue; "Save & exit" keeps the place.
//
// 🚨 DOORLYPRO NEVER NAMES THE BORROWER'S STEP NUMBERS — it says "your borrower's application".
// ⚠️ NO FIELD LIST HERE: labels, options, reveals and requiredness come from GET /submissions/schema; the Documents
// rows, the calculated lines and the Review rows come from the draft's own payload (backend utils/loSubmission*).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GuidedProgress from '../../guided/GuidedProgress';
import { formerRevealedFromVocabulary, yearOptions, monthOptions } from '../../../constants/addressHistory';
// 🔑 The step 1–3 questions' labels on this form are the one definition's LO labels (constants/applicationQuestions, 2026-10-29).
import { loLabel } from '../../../constants/applicationQuestions';
import { TextInput, MoneyInput, SelectInput, Segmented, DobInput, AddressInput, AddressParts, ZipField, FieldShell, targetId } from './LoFields';
import LoReviewRows from './LoReviewRows';
import { LO_ESTIMATE_NOTE } from '../../../constants/downPaymentEstimate';
import './loSubmission.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function LoSubmissionFlow({ token, schema, draftId, initialStep, onExit, onSubmitted, onStep, blocked }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);
  const flow = useMemo(() => schema.flow || {}, [schema]);
  const [payload, setPayload] = useState(null);
  const [values, setValues] = useState({});
  const [attested, setAttested] = useState({});
  const [step, setStep] = useState(initialStep || 'about');
  const [rentalN, setRentalN] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [scrollTo, setScrollTo] = useState(null);
  // 🔑 WHAT THIS STEP STILL NEEDS, as the server said when Continue was refused (stepBlocked). Jesse 2026-11-01.
  const [stepMissing, setStepMissing] = useState(null);

  // ── the draft ──
  const load = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/doorlypro/submissions/draft/${draftId}`, { headers });
    if (!r.ok) { setError('This draft could not be opened.'); return null; }
    const p = await r.json();
    setPayload(p);
    return p;
  }, [draftId, headers]);
  useEffect(() => {
    load().then((p) => { if (p) { setValues(p.draft.values || {}); setAttested(p.draft.attested || {}); if (!initialStep && p.draft.step_key) setStep(p.draft.step_key); } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  // 🔑 "Reading…": while any document is being read, the draft is asked again until every reading lands.
  const anyReading = !!payload && [...(payload.checklist.sections || []), ...(payload.checklist.others || [])]
    .some((s) => s.rows.some((r) => (r.files || []).some((f) => f.status === 'reading')));
  useEffect(() => {
    if (!anyReading) return undefined;
    const t = setTimeout(() => { load(); }, 2500);
    return () => clearTimeout(t);
  }, [anyReading, payload, load]);

  // ── the fields, from the schema ──
  const fieldsByKey = useMemo(() => Object.fromEntries((schema.fields || []).map((f) => [f.key, f])), [schema]);
  const meta = useCallback((key, extra = {}) => {
    const f = fieldsByKey[key] || { key, label: key };
    const refusal = (f.ineligible || []).find((i) => i.value === String(values[key] ?? '').trim());
    // A required field left blank on a refused Continue reads "Required" until it is answered.
    const required = !refusal && stepMissing && stepMissing.missing.some((m) => (m.keys || [m.key]).includes(key))
      && String(values[key] ?? '').trim() === '';
    return { ...f, label: (flow.shortLabels || {})[key] || f.label, help: (flow.helperText || {})[key] || f.note || null,
      refusal: refusal ? refusal.message : required ? 'Required' : null, ...extra };
  }, [fieldsByKey, flow, values, stepMissing]);
  // 🔑 THE REVEAL RULE IS THE SERVER'S (revealed_by), run the way constants/submissionVocabulary.isRevealed runs it:
  // a follow-up shows only while its parent shows; a multi-select parent reveals on any chosen value.
  const visible = useCallback(function vis(key) {
    const f = fieldsByKey[key];
    const r = f && f.revealed_by;
    if (!r) return true;
    if (r.addressHistory) return formerRevealedFromVocabulary(values, r.addressHistory);
    if (fieldsByKey[r.field] && fieldsByKey[r.field].revealed_by && !vis(r.field)) return false;
    const v = String(values[r.field] ?? '').trim();
    if (!v) return false;
    if (r.present) return true;
    return v.split(',').map((x) => x.trim()).some((x) => (r.values || []).includes(x));
  }, [fieldsByKey, values]);
  const set = (k, v) => { if (blocked) return; setValues((p) => ({ ...p, [k]: v })); };
  const setMany = (o) => { if (blocked) return; setValues((p) => ({ ...p, ...o })); };
  const pick = (base, { street, city, state, zip }) => setMany({ [`${base}street`]: street || '', [`${base}city`]: city || '', [`${base}state`]: state || '', [`${base}zip`]: zip || '' });

  // ── the steps ──
  const coYes = ['yes', 'true'].includes(String(values['coborrower.present'] || '').toLowerCase());
  const steps = (flow.steps || []).filter((s) => !s.when || (s.when === 'coborrower' && coYes));
  const idx = Math.max(0, steps.findIndex((s) => s.key === step));
  const types = String(values['employment.type'] || '').split(',').map((x) => x.trim()).filter(Boolean);
  const rentalCount = [1, 2, 3, 4].filter((n) => String(values[`rental.address_${n}`] || '').trim()).length;

  const save = async (nextStep, { exit = false, advanceFrom = null } = {}) => {
    setSaving(true); setError(null);
    try {
      const r = await fetch(`${API_URL}/api/doorlypro/submissions/draft/${draftId}`, { method: 'PUT', headers,
        body: JSON.stringify({ state: { values, attested }, step_key: nextStep, ...(advanceFrom ? { advance_from: advanceFrom } : {}) }) });
      const p = await r.json();
      if (!r.ok) { setError(p.message || p.error || 'Your draft could not be saved. Try again.'); return false; }
      setPayload(p);
      if (exit) { onExit('drafts'); return true; }
      // 🔑 REFUSED: this step still has a required field blank. Stay, name them, mark them.
      if (p.stepBlocked) { setStepMissing(p.stepBlocked); window.scrollTo(0, 0); return false; }
      setStepMissing(null);
      setStep(nextStep);
      if (onStep) onStep(nextStep);
      window.scrollTo(0, 0);
      return true;
    } catch (_) { setError('Could not reach Doorly. Your draft was not saved.'); return false; } finally { setSaving(false); }
  };
  // 🔑 A METHOD CHOSEN OR CHANGED IN DOCUMENTS IS SAVED AT ONCE, so the rows below are the server's for the new
  // method (the picker swaps the rows). Quiet: no step change, no scroll.
  const methodSig = ['employment.self_employed_path', 'employment.bank_statement_months', ...[1, 2, 3, 4].map((n) => `rental.method_${n}`)]
    .map((k) => values[k] || '').join('|');
  const lastSig = useRef(null);
  useEffect(() => {
    if (step !== 'documents' || !payload) { lastSig.current = methodSig; return undefined; }
    if (lastSig.current === null || lastSig.current === methodSig) { lastSig.current = methodSig; return undefined; }
    lastSig.current = methodSig;
    const t = setTimeout(() => {
      fetch(`${API_URL}/api/doorlypro/submissions/draft/${draftId}`, { method: 'PUT', headers, body: JSON.stringify({ state: { values, attested }, step_key: 'documents' }) })
        .then((r) => (r.ok ? r.json() : null)).then((p) => { if (p) setPayload(p); }).catch(() => {});
    }, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methodSig, step]);
  const next = () => {
    if (step === 'employment' && types.includes('rental') && rentalN < Math.max(1, rentalCount)) { setRentalN(rentalN + 1); window.scrollTo(0, 0); return; }
    const n = steps[idx + 1];
    if (n) { setRentalN(0); save(n.key, { advanceFrom: step }); }
  };
  const back = () => {
    if (step === 'employment' && rentalN > 0) { setRentalN(rentalN - 1); return; }
    const p = steps[idx - 1];
    if (p) { setRentalN(p.key === 'employment' && types.includes('rental') ? Math.max(1, rentalCount) : 0); save(p.key); }
  };
  // 🔑 "FIX THIS" AND "EDIT": the step that holds the row or answer, then the row itself.
  const goTo = (target) => {
    if (!target) return;
    setRentalN(target.step === 'employment' && /^rental\./.test(target.target || '') ? 1 : 0);
    save(target.step).then((ok) => { if (ok) setScrollTo(target.target || null); });
  };
  useEffect(() => {
    if (!scrollTo) return;
    const t = setTimeout(() => {
      const el = document.getElementById(targetId(scrollTo));
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.setAttribute('data-fix-target', 'true'); }
      setScrollTo(null);
    }, 150);
    return () => clearTimeout(t);
  }, [scrollTo, step]);

  if (error && !payload) return <div style={{ padding: 40, color: '#b3321f' }}>{error}</div>;
  if (!payload) return <div style={{ padding: 40, color: '#6b7280' }}>Loading…</div>;

  const label = steps[idx] ? steps[idx].label : '';
  const headLabel = step === 'employment' && rentalN > 0 ? `${label} · rental ${rentalN} of ${Math.max(rentalN, rentalCount)}` : label;
  const position = `${idx + 1} of ${steps.length}`;

  return (
    <div data-testid="lo-flow" data-step={step} style={{ maxWidth: 880, margin: '0 auto', padding: '24px 16px 80px' }}>
      <div className="lo-step-head">
        <div style={{ minWidth: 0 }}>
          <div data-testid="lo-step-position" style={{ fontSize: 13, color: '#374151' }}>{position}</div>
          <div data-testid="lo-step-title" style={{ fontSize: 18, fontWeight: 600, color: '#111827', marginTop: 2 }}>{headLabel}</div>
        </div>
        <button type="button" className="lo-btn lo-btn-ghost" data-testid="lo-save-exit" disabled={saving}
          style={{ height: 40, padding: '0 14px', fontSize: 13.5 }} onClick={() => save(step, { exit: true })}>Save &amp; exit</button>
      </div>
      <div style={{ margin: '12px 0 20px' }}><GuidedProgress step={idx + 1} total={steps.length} valueText={position} /></div>

      <div className="lo-card" data-testid={`lo-card-${step}`}>
        {stepMissing && stepMissing.step === step && (() => {
          const still = stepMissing.missing.filter((m) => (m.keys || [m.key]).some((k) => String(values[k] ?? '').trim() === ''));
          return still.length > 0 && (
            <div data-testid="lo-step-missing" role="alert" style={{ margin: '0 0 16px', padding: '10px 14px', borderRadius: 8,
              background: '#fff0f3', border: '1px solid #fecdd3', color: '#9f1239', fontSize: 13.5, lineHeight: 1.5 }}>
              Fill in these to continue: {still.map((m) => m.label).join(', ')}.
            </div>
          );
        })()}
        {step === 'about' && <AboutStep {...{ meta, values, set, pick, visible }} />}
        {step === 'home' && <HomeStep {...{ meta, values, set, setMany, token, schema, attested, setAttested }} />}
        {step === 'coborrower' && <CoborrowerStep {...{ meta, values, set }} />}
        {step === 'employment' && rentalN === 0 && <EmploymentStep {...{ meta, values, set, visible, flow }} />}
        {step === 'employment' && rentalN > 0 && <RentalScreen n={rentalN} {...{ meta, values, set, setMany, onAdd: () => setRentalN(rentalN + 1) }} />}
        {step === 'documents' && <DocumentsStep {...{ payload, values, set, setMany, flow, token, draftId, reload: load, setPayload, goTo }} />}
        {step === 'calculated' && <CalculatedStep calc={payload.calculated} goTo={goTo} />}
        {step === 'review' && <ReviewStep {...{ payload, goTo, attested, schema, token, draftId, onSubmitted, blocked }} />}
        {error && <p style={{ margin: '16px 0 0', fontSize: 13, color: '#b3321f' }}>{error}</p>}
        {step !== 'review' && (
          <div className="lo-nav">
            {idx > 0 || rentalN > 0 ? <button type="button" className="lo-btn lo-btn-ghost" data-testid="lo-back" disabled={saving} onClick={back}>Back</button> : <span />}
            <button type="button" className="lo-btn lo-btn-primary" data-testid="lo-continue" disabled={saving || blocked} onClick={next}>
              {saving ? 'Saving…' : 'Continue'}
            </button>
          </div>
        )}
        {step === 'review' && (
          <div className="lo-nav"><button type="button" className="lo-btn lo-btn-ghost" data-testid="lo-back" disabled={saving} onClick={back}>Back</button><span /></div>
        )}
      </div>
    </div>
  );
}

const H2 = ({ children, sub }) => (
  <>
    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>{children}</h2>
    {sub && <p style={{ fontSize: 14, color: '#6b7280', margin: '6px 0 20px', lineHeight: 1.55 }}>{sub}</p>}
    {!sub && <div style={{ height: 20 }} />}
  </>
);

// ── About your borrower ─────────────────────────────────────────────────────────────────────────────────────────
function AboutStep({ meta, values, set, pick, visible }) {
  const itin = values['borrower.tax_id_type'] === 'itin';
  return (
    <>
      <H2 sub="Who they are and where they live now.">About your borrower</H2>
      <div className="lo-grid">
        <TextInput meta={meta('borrower.first_name', { label: loLabel('borrower.first_name') })} value={values['borrower.first_name']} onChange={(v) => set('borrower.first_name', v)} />
        <TextInput meta={meta('borrower.middle_name', { label: loLabel('borrower.middle_name') })} value={values['borrower.middle_name']} placeholder="Optional" onChange={(v) => set('borrower.middle_name', v)} />
        <TextInput meta={meta('borrower.last_name', { label: loLabel('borrower.last_name') })} value={values['borrower.last_name']} onChange={(v) => set('borrower.last_name', v)} />
        <DobInput meta={meta('borrower.date_of_birth', { label: loLabel('borrower.date_of_birth') })} value={values['borrower.date_of_birth']} onChange={(v) => set('borrower.date_of_birth', v)} />
        <Segmented meta={meta('borrower.tax_id_type', { label: loLabel('borrower.tax_id_type') })} value={values['borrower.tax_id_type']} onChange={(v) => set('borrower.tax_id_type', v)} />
        {values['borrower.tax_id_type'] && (
          <TextInput meta={meta(itin ? 'borrower.itin' : 'borrower.ssn', { label: loLabel(itin ? 'borrower.itin' : 'borrower.ssn') })} span={2} inputMode="numeric"
            value={values[itin ? 'borrower.itin' : 'borrower.ssn']} placeholder="123-45-6789"
            onChange={(v) => set(itin ? 'borrower.itin' : 'borrower.ssn', v.replace(/[^0-9-]/g, '').slice(0, 11))} />
        )}
        {!values['borrower.tax_id_type'] && <span className="lo-span-2" />}
        <TextInput meta={meta('borrower.email', { label: loLabel('borrower.email') })} type="email" value={values['borrower.email']} onChange={(v) => set('borrower.email', v)} />
        <TextInput meta={meta('borrower.phone', { label: loLabel('borrower.phone') })} type="tel" value={values['borrower.phone']} onChange={(v) => set('borrower.phone', v)} />
        <SelectInput meta={meta('borrower.citizenship', { label: loLabel('borrower.citizenship') })} value={values['borrower.citizenship']} onChange={(v) => set('borrower.citizenship', v)} />
        <SelectInput meta={meta('borrower.marital_status', { label: loLabel('borrower.marital_status') })} value={values['borrower.marital_status']} onChange={(v) => set('borrower.marital_status', v)} />
        <TextInput meta={meta('borrower.dependents_count', { label: loLabel('borrower.dependents_count') })} inputMode="numeric" value={values['borrower.dependents_count']} placeholder="0"
          onChange={(v) => set('borrower.dependents_count', v.replace(/\D/g, '').slice(0, 2))} />
        <TextInput meta={meta('borrower.dependents_ages', { label: loLabel('borrower.dependents_ages') })} value={values['borrower.dependents_ages']} placeholder="6, 9"
          onChange={(v) => set('borrower.dependents_ages', v)} />
        <AddressInput meta={meta('borrower.address_street', { label: loLabel('borrower.address_street') })} values={values} base="borrower.address_" onChange={set} onPick={pick} />
        <AddressParts values={values} base="borrower.address_" onChange={set} />
        <SelectInput meta={meta('borrower.address_years', { label: loLabel('borrower.address_years') })} options={yearOptions()} placeholder="Years" value={values['borrower.address_years']} onChange={(v) => set('borrower.address_years', v)} />
        <SelectInput meta={meta('borrower.address_months', { label: loLabel('borrower.address_months') })} options={monthOptions()} placeholder="Months" value={values['borrower.address_months']} onChange={(v) => set('borrower.address_months', v)} />
        <span />
      </div>
      {[1, 2, 3].filter((n) => visible(`borrower.prev_address_${n}_street`)).map((n) => (
        <div key={n} className="lo-sub" data-testid={`lo-former-${n}`}>
          <div className="lo-sub-title">Former address {n}</div>
          {n === 1 && <p className="lo-help" style={{ margin: '0 0 12px' }}>Under 2 years at the current address, so we need where they lived before (up to 3 former addresses).</p>}
          <div className="lo-grid">
            <AddressInput meta={meta(`borrower.prev_address_${n}_street`, { label: loLabel('borrower.prev_address_1_street') })} values={values} base={`borrower.prev_address_${n}_`} onChange={set} onPick={pick} />
            <AddressParts values={values} base={`borrower.prev_address_${n}_`} onChange={set} />
            <SelectInput meta={meta(`borrower.prev_address_${n}_years`, { label: 'Years there' })} options={yearOptions()} placeholder="Years" value={values[`borrower.prev_address_${n}_years`]} onChange={(v) => set(`borrower.prev_address_${n}_years`, v)} />
            <SelectInput meta={meta(`borrower.prev_address_${n}_months`, { label: 'Months' })} options={monthOptions()} placeholder="Months" value={values[`borrower.prev_address_${n}_months`]} onChange={(v) => set(`borrower.prev_address_${n}_months`, v)} />
            <span />
            <Segmented meta={meta(`borrower.prev_address_${n}_housing_status`, { label: 'Housing at this address' })} span="all"
              value={values[`borrower.prev_address_${n}_housing_status`]} onChange={(v) => set(`borrower.prev_address_${n}_housing_status`, v)} />
          </div>
        </div>
      ))}
      <div className="lo-sub">
        <div className="lo-grid">
          <Segmented meta={meta('borrower.housing_status', { label: loLabel('borrower.housing_status') })} span="all" value={values['borrower.housing_status']} onChange={(v) => set('borrower.housing_status', v)} />
          {['current_home.has_mortgage', 'current_home.mortgage_holder', 'current_home.plan', 'current_home.rent_out'].filter(visible).map((k) => (
            <Segmented key={k} meta={meta(k, { label: loLabel(k) })}
            span="all" value={values[k]} onChange={(v) => set(k, v)} />
          ))}
          <Segmented meta={meta('coborrower.present', { label: loLabel('coborrower.present') })} span="all" value={values['coborrower.present']} onChange={(v) => set('coborrower.present', v)} />
        </div>
      </div>
    </>
  );
}

// ── The home and the loan ───────────────────────────────────────────────────────────────────────────────────────
function HomeStep({ meta, values, set, setMany, token, schema, attested, setAttested }) {
  const price = Number(values['loan.purchase_price'] || 0); const down = Number(values['loan.down_payment'] || 0);
  const loan = price > 0 && price - down > 0 ? String(price - down) : '';
  const elig = (schema.attestations || []).filter((a) => !a.whenCredit);
  const creditAtt = (schema.attestations || []).filter((a) => a.whenCredit);
  const carriesCredit = (schema.creditKeys || []).some((k) => String(values[k] ?? '').trim());
  const confirm = (a, on) => {
    setAttested((p) => ({ ...p, [a.key]: on }));
    // 🔑 THE CONFIRMATION IS THE ANSWER: a manual file is a purchase of a primary residence, asserted here.
    if (!a.whenCredit) setMany({ 'loan.purpose': on ? 'purchase' : '', 'subject_property.occupancy': on ? 'owner_occupied' : '' });
  };
  return (
    <>
      <H2>The home and the loan</H2>
      <div className="lo-grid">
        <MoneyInput meta={meta('loan.purchase_price')} value={values['loan.purchase_price']} onChange={(v) => set('loan.purchase_price', v)} />
        <MoneyInput meta={meta('loan.down_payment')} value={values['loan.down_payment']} onChange={(v) => set('loan.down_payment', v)} />
        <MoneyInput meta={meta('loan.amount')} value={loan} readOnly />
        <SelectInput meta={meta('property.type')} value={values['property.type']} onChange={(v) => set('property.type', v)} />
        <SelectInput meta={meta('subject_property.state')} value={values['subject_property.state']} onChange={(v) => set('subject_property.state', v)} />
        <SelectInput meta={meta('loan.timeline')} value={values['loan.timeline']} onChange={(v) => set('loan.timeline', v)} />
        <ZipField meta={meta('subject_property.target_zips')} value={values['subject_property.target_zips']} onChange={(v) => set('subject_property.target_zips', v)} />
      </div>
      <MinimumDown token={token} values={values} />
      {[...elig, ...(carriesCredit ? creditAtt : [])].map((a) => (
        <div key={a.key} className="lo-sub" data-testid={`lo-attest-${a.key}`}>
          <div className="lo-sub-title">{a.whenCredit ? a.label : 'Purchase, primary residence'}</div>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
            <input type="checkbox" checked={!!attested[a.key]} onChange={(e) => confirm(a, e.target.checked)} style={{ marginTop: 4, width: 18, height: 18 }} />
            <span>{a.text}</span>
          </label>
        </div>
      ))}
    </>
  );
}

// 🔑 THE MINIMUM DOWN PAYMENT, A SENTENCE THE SERVER WROTE (GET /submissions/down-payment — utils/minimumDownPayment).
function MinimumDown({ token, values }) {
  const [res, setRes] = useState(null);
  const price = values['loan.purchase_price']; const down = values['loan.down_payment'];
  const sc = ['credit.score_equifax', 'credit.score_experian', 'credit.score_transunion'].map((k) => values[k] || '');
  // The declared income path: a bank-statement path takes the bank-statement minimum (utils/bankStatementFile).
  const sePath = values['employment.self_employed_path'] || '';
  useEffect(() => {
    if (!String(price || '').trim()) { setRes(null); return undefined; }
    const t = setTimeout(() => {
      const q = new URLSearchParams({ price: String(price), down: String(down || ''), eq: sc[0], ex: sc[1], tu: sc[2], path: sePath });
      fetch(`${API_URL}/api/doorlypro/submissions/down-payment?${q}`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json()).then(setRes).catch(() => setRes(null));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, price, down, sc[0], sc[1], sc[2], sePath]);
  if (!res || res.state === 'no_price' || res.state === 'unavailable') return null;
  if (res.state === 'price_out_of_band') return <p className="lo-refusal" style={{ marginTop: 16 }}>{res.message}</p>;
  const short = res.down !== null && !res.meets_minimum;
  return (
    <>
    <p data-testid="lo-min-down" style={{ margin: '16px 0 0', fontSize: 13.5, lineHeight: 1.6, color: short ? '#92400e' : '#374151' }}>
      Minimum down payment on this file: {Math.round(res.min_pct * 100)}% · ${Number(res.min_amount).toLocaleString('en-US')}.
      {res.down !== null && res.down > 0 ? ` ${res.meets_minimum ? `$${Number(res.down).toLocaleString('en-US')} clears it.` : res.message}` : ''}
      {/* ⚠️ A SHORTFALL IS NOT A REFUSAL: the figure moves with a verified score, and nothing here declines a file. */}
      {short && ' Send the file anyway if that is what the borrower has \u2014 this is what the file shows today, not a decision.'}
    </p>
    {/* 🔑 THE DOWN-PAYMENT ESTIMATE (Jesse, 2026-10-29 — constants/downPaymentEstimate): the same paragraph style. */}
    <p data-testid="lo-min-down-estimate" style={{ margin: '4px 0 0', fontSize: 13.5, lineHeight: 1.6, color: '#374151' }}>{LO_ESTIMATE_NOTE}</p>
    </>
  );
}

// ── Co-borrower ─────────────────────────────────────────────────────────────────────────────────────────────────
function CoborrowerStep({ meta, values, set }) {
  return (
    <>
      <H2 sub="They get their own invitation and confirm their own details in their application.">Co-borrower</H2>
      <div className="lo-grid lo-cols-2">
        {['first_name', 'last_name', 'email', 'phone'].map((p) => (
          <TextInput key={p} meta={meta(`coborrower.${p}`, { label: loLabel(`coborrower.${p}`) })}
            value={values[`coborrower.${p}`]} onChange={(v) => set(`coborrower.${p}`, v)} />
        ))}
      </div>
    </>
  );
}

// ── Employment and income: which sources only ──────────────────────────────────────────────────────────────────
function EmploymentStep({ meta, values, set, visible, flow }) {
  const f = meta('employment.type');
  const chosen = String(values['employment.type'] || '').split(',').filter(Boolean);
  const toggle = (v, on) => set('employment.type', (on ? [...chosen, v] : chosen.filter((x) => x !== v)).join(','));
  return (
    <>
      <H2 sub="Check all that apply. Each one adds the documents your borrower will be asked for.">Employment and income</H2>
      <div className="lo-pick" id={targetId('employment.type')}>
        {(f.options || []).map((o) => {
          const on = chosen.includes(o.value);
          return (
            <label key={o.value} className="lo-pick-opt" aria-pressed={on ? 'true' : 'false'} data-testid={`lo-source-${o.value}`} style={{ display: 'flex', gap: 10 }}>
              <input type="checkbox" checked={on} onChange={(e) => toggle(o.value, e.target.checked)} style={{ marginTop: 3, width: 18, height: 18 }} />
              <span>
                <span style={{ display: 'block', fontSize: 14.5, fontWeight: 700, color: '#111827' }}>{o.label}</span>
                <span style={{ display: 'block', fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>{(flow.sourceHints || {})[o.value] || o.hint}</span>
              </span>
            </label>
          );
        })}
      </div>
      {visible('employment.job_title') && (
        <div className="lo-sub">
          <div className="lo-sub-title">The job</div>
          <div className="lo-grid" style={{ marginTop: 10 }}>
            <TextInput meta={meta('employment.job_title', { label: loLabel('employment.job_title') })} span={2} value={values['employment.job_title']} onChange={(v) => set('employment.job_title', v)} />
          </div>
        </div>
      )}
      {visible('employment.business_name') && (
        <div className="lo-sub">
          <div className="lo-sub-title">The business</div>
          <div className="lo-grid" style={{ marginTop: 10 }}>
            <TextInput meta={meta('employment.business_name')} value={values['employment.business_name']} onChange={(v) => set('employment.business_name', v)} />
            <SelectInput meta={meta('employment.business_years', { label: 'Years in business' })} options={yearOptions()} placeholder="Years" value={values['employment.business_years']} onChange={(v) => set('employment.business_years', v)} />
            <SelectInput meta={meta('employment.business_months', { label: 'Months' })} options={monthOptions()} placeholder="Months" value={values['employment.business_months']} onChange={(v) => set('employment.business_months', v)} />
          </div>
        </div>
      )}
    </>
  );
}

// ── One screen per rental property ──────────────────────────────────────────────────────────────────────────────
function RentalScreen({ n, meta, values, set, onAdd }) {
  const k = (p) => `rental.${p}_${n}`;
  const [street, setStreet] = useState(values[k('address')] || '');
  useEffect(() => { setStreet(values[k('address')] || ''); }, [n]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <H2>{`Rental property ${n}`}</H2>
      <div className="lo-grid">
        <FieldShell fkey={k('address')} label="Address" span="all">
          <input className="lo-input" name={k('address')} value={street} placeholder="412 Elm St, Austin, TX 78702"
            onChange={(e) => { setStreet(e.target.value); set(k('address'), e.target.value); }} />
        </FieldShell>
        <Segmented meta={meta(k('rented'), { label: 'Is it rented now?' })} span="all" value={values[k('rented')]} onChange={(v) => set(k('rented'), v)} />
        <Segmented meta={meta(k('mortgage'), { label: 'Is there a mortgage on it?' })} span="all" value={values[k('mortgage')]} onChange={(v) => set(k('mortgage'), v)} />
      </div>
      {n < 4 && String(values[k('address')] || '').trim() && (
        <div style={{ marginTop: 16 }}><button type="button" className="lo-link" data-testid="lo-add-rental" onClick={onAdd}>Add another rental property</button></div>
      )}
    </>
  );
}

// ── Documents ───────────────────────────────────────────────────────────────────────────────────────────────────
function DocumentsStep({ payload, values, set, setMany, flow, token, draftId, reload, setPayload, goTo }) {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const inputs = useRef({});
  const upload = async (rowKey, docType, fileList) => {
    const files = [...(fileList || [])];
    if (!files.length) return;
    setBusy(rowKey); setErr(null);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file); fd.append('document_type', docType); fd.append('row_key', rowKey);
        const r = await fetch(`${API_URL}/api/doorlypro/submissions/draft/${draftId}/documents`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
        if (!r.ok) { const b = await r.json().catch(() => ({})); setErr(b.message || b.error || 'That file was not added.'); break; }
      }
      await reload();
    } finally { setBusy(null); }
  };
  const remove = async (ids, reason) => {
    const r = await fetch(`${API_URL}/api/doorlypro/submissions/draft/${draftId}/documents/${ids.join(',')}${reason ? `?reason=${reason}` : ''}`,
      { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) setPayload(await r.json());
  };
  // 🔑 refKey (2026-10-29): a second upload on one row (the "both rows" letter) keeps its own input and test id.
  const fileButton = (rowKey, docType, text = 'Upload', primary = true, refKey = rowKey) => (
    <>
      <input type="file" multiple style={{ display: 'none' }} data-testid={`lo-file-${refKey}`} ref={(el) => { inputs.current[refKey] = el; }}
        onChange={(e) => { upload(rowKey, docType, e.target.files); e.target.value = ''; }} />
      <button type="button" className={primary ? 'lo-btn lo-btn-primary' : 'lo-btn lo-btn-ghost'} data-testid={`lo-upload-${refKey}`}
        style={{ height: 40, padding: '0 16px', fontSize: 13.5, flexShrink: 0 }} disabled={busy === rowKey}
        onClick={() => inputs.current[refKey] && inputs.current[refKey].click()}>{busy === rowKey ? 'Adding…' : text}</button>
    </>
  );
  const sections = payload.checklist.sections || [];
  const others = payload.checklist.others || [];
  return (
    <>
      <H2 sub="The same list your borrower gets in their application, from your answers. Each document is read when you add it.">Documents</H2>
      {err && <p className="lo-refusal" style={{ marginBottom: 12 }}>{err}</p>}
      {[...sections, ...others].map((s) => (
        <section key={s.key} data-testid={`lo-docs-${s.key}`} style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>
            {s.title}{s.optional && s.key === 'housing' ? <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 13 }}> · optional here</span> : null}
          </div>
          {s.picker && <MethodPicker picker={s.picker} values={values} set={set} setMany={setMany} flow={flow} />}
          {(!s.picker || s.picker.chosen) && s.rows.map((r) => (
            <div key={r.rowKey} className="lo-row" id={targetId(r.rowKey)} data-testid={`lo-row-${r.rowKey}`} data-done={r.done ? 'true' : 'false'}>
              <div className={`lo-row-icon${r.done ? ' done' : ''}`} aria-hidden="true">
                {(r.files || []).some((f) => f.status === 'reading') ? '◔' : r.done ? '✓' : '○'}
              </div>
              <div className="lo-row-main">
                <div className="lo-row-title">{r.label}</div>
                <div className="lo-row-sub" data-testid={`lo-row-line-${r.rowKey}`}>{r.readLine || r.note}</div>
                {(r.accounts || []).map((a) => <div key={a.label} className="lo-row-sub">{a.read ? '✓' : '○'} Latest statement · {a.label}</div>)}
                {(r.files || []).length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    {r.files.map((f) => (
                      <div key={f.id} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 12.5, color: '#374151', marginTop: 2 }}>
                        <span style={{ overflowWrap: 'anywhere' }}>{f.name}</span>
                        <span style={{ color: '#6b7280' }}>{f.status === 'reading' ? 'Reading…' : f.status === 'read' ? 'Read' : f.status === 'unreadable' ? 'Couldn’t be read' : 'Added'}</span>
                        <button type="button" className="lo-link" style={{ fontSize: 12.5 }} onClick={() => remove([f.id])}>Remove</button>
                      </div>
                    ))}
                  </div>
                )}
                {r.bothRows && s.rows.some((x) => x.id === r.bothRows.rowId) && (
                  <div className="lo-row-sub" data-testid={`lo-both-rows-${r.rowKey}`} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                    <span>{r.bothRows.label}</span>
                    {fileButton(r.rowKey, r.bothRows.docType, 'Add the letter', false, `${r.rowKey}__both`)}
                  </div>
                )}
                {(r.notes || []).map((n, i) => (
                  <div key={i} className={`lo-note${n.open ? ' open' : ''}`} id={n.rowKey ? targetId(n.rowKey) : undefined} data-testid={`lo-note-${n.kind}`}>
                    <div>{n.text}</div>
                    {(n.actions || []).length > 0 && (
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6, alignItems: 'center' }}>
                        {n.actions.map((a) => (a.id === 'different_account'
                            ? <button key={a.id} type="button" className="lo-link" onClick={() => remove((r.files || []).map((f) => f.id), 'different_account')}>{a.label}</button>
                            // 🔑 an excluded deposit's optional document (backend utils/depositSourcing): filed on the deposit's own row key
                            : a.id === 'add_source' ? <span key={a.id}>{fileButton(a.rowKey, a.docType, a.label, false)}</span>
                            : <button key={a.id} type="button" className="lo-link" onClick={() => goTo(a.target)}>{a.label}</button>))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {fileButton(r.rowKey, r.docType, r.done ? 'Add another' : 'Upload', !r.done)}
            </div>
          ))}
        </section>
      ))}
    </>
  );
}

// 🔑 THE METHOD PICKER, AT THE TOP OF EACH INCOME SECTION: the choices the first time, then "Showing … · Change".
// 12 or 24 months only under a bank-statement choice. Schedule E hidden, with its reason, when bank statements are used.
function MethodPicker({ picker, values, set, setMany, flow }) {
  const [open, setOpen] = useState(!picker.chosen);
  useEffect(() => { if (!picker.chosen) setOpen(true); }, [picker.chosen]);
  const id = picker.kind === 'se' ? 'se:method' : `rental:${picker.n}:method`;
  if (!open && picker.chosen) {
    return (
      <div id={targetId(id)} data-testid={`lo-method-${id}`} style={{ fontSize: 13.5, color: '#374151', margin: '0 0 10px' }}>
        Showing {picker.line} · <button type="button" className="lo-link" data-testid={`lo-method-change-${id}`} onClick={() => setOpen(true)}>Change</button>
      </div>
    );
  }
  const months = flow.bankStatementMonths || [12, 24];
  const sePath = values['employment.self_employed_path'];
  return (
    <div id={targetId(id)} data-testid={`lo-picker-${id}`} style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 14, color: '#111827', fontWeight: 600, margin: '0 0 8px' }}>
        {picker.kind === 'se' ? 'How will your borrower show their self-employed income?' : 'How will your borrower show the rent?'}
      </div>
      <div className="lo-pick">
        {picker.options.map((o) => {
          const on = picker.kind === 'se'
            ? (o.paths ? o.paths.some((p) => p.id === sePath) : sePath === o.id)
            : values[`rental.method_${picker.n}`] === o.id;
          return (
            <div key={o.id} className="lo-pick-opt" aria-pressed={on ? 'true' : 'false'} data-testid={`lo-pick-${id}-${o.id}`} role="button" tabIndex={0}
              onClick={() => {
                if (picker.kind === 'se') {
                  if (o.paths) { if (!on) setMany({ 'employment.self_employed_path': o.paths[0].id, 'employment.bank_statement_months': '' }); }
                  else setMany({ 'employment.self_employed_path': o.id, ...(o.months ? {} : { 'employment.bank_statement_months': '' }) });
                } else set(`rental.method_${picker.n}`, o.id);
              }}>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: '#111827' }}>{o.label}</div>
              <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>{o.desc}</div>
              {on && o.months && (
                <div className="lo-seg" style={{ marginTop: 10, maxWidth: 320 }} onClick={(e) => e.stopPropagation()}>
                  {months.map((m) => (
                    <button key={m} type="button" className="lo-seg-btn" aria-pressed={String(values['employment.bank_statement_months']) === String(m) ? 'true' : 'false'}
                      data-testid={`lo-months-${m}`} onClick={() => set('employment.bank_statement_months', String(m))}>{m} months</button>
                  ))}
                </div>
              )}
              {on && o.paths && (
                <div className="lo-seg" style={{ marginTop: 10 }} onClick={(e) => e.stopPropagation()}>
                  {o.paths.map((p) => (
                    <button key={p.id} type="button" className="lo-seg-btn" aria-pressed={sePath === p.id ? 'true' : 'false'}
                      onClick={() => set('employment.self_employed_path', p.id)}>{p.label}</button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {picker.hiddenReason && <p className="lo-help" data-testid="lo-schedule-e-hidden">{picker.hiddenReason}</p>}
      {picker.kind === 'se' && <p className="lo-help">Your borrower sees this in their application and can change it. The list below changes with it.</p>}
      {(picker.kind === 'se' ? (sePath && (!['bank_stmt_personal', 'bank_stmt_business'].includes(sePath) || values['employment.bank_statement_months'])) : values[`rental.method_${picker.n}`]) && (
        <button type="button" className="lo-link" style={{ marginTop: 8 }} data-testid={`lo-method-done-${id}`} onClick={() => setOpen(false)}>Show the documents</button>
      )}
    </div>
  );
}

// ── Calculated income & assets ─────────────────────────────────────────────────────────────────────────────────
export function CalculatedLines({ calc, goTo, frozen = false }) {
  return (
    <>
      {calc.lines.map((l) => (
        <div key={l.key} className="lo-calc" data-testid={`lo-calc-${l.key}`}>
          <div className="lo-calc-top">
            <div className="lo-calc-label">{l.label}</div>
            <div className={`lo-calc-fig${l.notCounted ? ' not' : ''}`}>{l.reading ? 'Reading…' : l.notCounted ? 'Not counted yet' : l.figure}</div>
          </div>
          {!l.reading && <div className="lo-calc-why">{l.reason}</div>}
          {l.fix && (frozen ? <div className="lo-calc-why" style={{ fontWeight: 700 }}>Fix this</div>
            : <button type="button" className="lo-link" style={{ marginTop: 6 }} data-testid={`lo-fix-${l.key}`} onClick={() => goTo(l.fix)}>Fix this</button>)}
        </div>
      ))}
      {calc.assets && (
        <div className="lo-calc" data-testid="lo-calc-assets">
          <div className="lo-calc-top">
            <div className="lo-calc-label">{calc.assets.label}</div>
            <div className={`lo-calc-fig${calc.assets.figure ? '' : ' not'}`}>{calc.assets.reading ? 'Reading…' : calc.assets.figure || 'Not counted yet'}</div>
          </div>
          <div className="lo-calc-why">{calc.assets.reason}</div>
          {calc.assets.fix && !frozen && <button type="button" className="lo-link" style={{ marginTop: 6 }} onClick={() => goTo(calc.assets.fix)}>Fix this</button>}
        </div>
      )}
      {calc.openLine && <p data-testid="lo-open-line" className="lo-note open" style={{ marginTop: 12 }}>{calc.openLine}</p>}
      <p data-testid="lo-calc-footer" style={{ fontSize: 12.5, color: '#6b7280', margin: '14px 0 0' }}>{calc.footer}</p>
    </>
  );
}
function CalculatedStep({ calc, goTo }) {
  return (
    <>
      <H2 sub={calc.intro}>{calc.title}</H2>
      <CalculatedLines calc={calc} goTo={goTo} />
    </>
  );
}

// ── Review and submit ──────────────────────────────────────────────────────────────────────────────────────────
function ReviewStep({ payload, goTo, attested, schema, token, draftId, onSubmitted, blocked }) {
  const [submitting, setSubmitting] = useState(false);
  const [refusal, setRefusal] = useState(null);
  const missing = payload.review.missingRequired || [];
  const owed = (schema.attestations || []).filter((a) => !a.whenCredit || (schema.creditKeys || []).some((k) => String(payload.draft.values[k] ?? '').trim()));
  const unattested = owed.filter((a) => !attested[a.key]);
  // 🚨 AN ANSWER THE PROGRAM WILL NOT TAKE (the server's ineligible list, with the matrix's sentence) holds the file.
  const refusals = (schema.fields || []).map((f) => ({ f, hit: (f.ineligible || []).find((i) => i.value === String(payload.draft.values[f.key] ?? '').trim()) }))
    .filter((x) => x.hit).map((x) => `${x.f.label}: ${x.hit.message}`);
  const disabled = blocked || submitting || missing.length > 0 || unattested.length > 0 || refusals.length > 0;
  const submit = async () => {
    setSubmitting(true); setRefusal(null);
    try {
      const r = await fetch(`${API_URL}/api/doorlypro/submissions`, { method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ format: 'manual', draft_id: draftId, attestations: attested }) });
      const d = await r.json();
      if (!r.ok) { setRefusal(d.message || 'Doorly could not accept this submission.'); return; }
      onSubmitted(d.submission_id);
    } catch (_) { setRefusal('Could not reach Doorly. Nothing was submitted — try again.'); } finally { setSubmitting(false); }
  };
  return (
    <>
      <H2 sub="Check each section. Your borrower confirms all of it in their application before we work the file.">Review and submit</H2>
      <LoReviewRows sections={payload.review.sections} onEdit={(s) => goTo({ step: s })} onView={() => goTo({ step: 'calculated' })} />
      {refusal && <p className="lo-refusal" style={{ marginTop: 12 }}>{refusal}</p>}
      {refusals.length > 0 && <p className="lo-refusal" data-testid="lo-ineligible" style={{ marginTop: 12 }}>{refusals.join(' ')}</p>}
      <button type="button" className="lo-btn lo-btn-primary" data-testid="lo-submit" disabled={disabled} onClick={submit} style={{ width: '100%', marginTop: 16 }}>
        {submitting ? 'Submitting…' : 'Submit application'}
      </button>
      {missing.length > 0 && <p data-testid="lo-still-needed" style={{ margin: '10px 0 0', fontSize: 12.5, color: '#b3321f', textAlign: 'center' }}>Still needed: {missing.join(', ')}</p>}
      {missing.length === 0 && unattested.length > 0 && (
        <p style={{ margin: '10px 0 0', fontSize: 12.5, color: '#6b7280', textAlign: 'center' }}>Confirm the purchase and primary residence on The home and the loan to submit.</p>
      )}
    </>
  );
}
